/**
 * Link-specific validator warm-up.
 *
 * A deep link like `?patient=123` tells us at bootstrap exactly which modal is
 * about to open, and therefore which endpoints will be requested — roughly a
 * second before their responses land. That is enough time to load messages.json
 * and compile those validators in idle slices, so `withValidator` finds them
 * already cached instead of building them on the critical path.
 *
 * Paths are the templated forms used as `pathsToMessage` keys (verified against
 * the generated messages.json). Note the two that do NOT carry the
 * `agency_members/:agencyMemberId` prefix, and that `caregivers_associated` sits
 * under `patient/` singular — copy them exactly or the warm-up silently no-ops
 * and the compile happens later on demand as before.
 *
 * This module intentionally imports nothing but `warmValidators`: it must stay
 * loadable at startup without pulling in a modal's module graph, so that
 * code-splitting the modals later doesn't drag them back into the entry chunk.
 */
import { warmValidators } from "./validate-message";

const MEMBER = "/agencies/:agencyId/agency_members/:agencyMemberId";
const AGENCY = "/agencies/:agencyId";

/** The 8 validators the patient profile modal compiles on first open. */
const PATIENT_MODAL_ENDPOINTS = [
  `${AGENCY}/patients/:patientId`, // PatientExtendedData — the largest compile
  `${MEMBER}/patients/:patientId/visit_instances_billing`, // PatientCalendarData
  `${MEMBER}/patients/:patientId/patient_task_instances_billing`, // PatientTaskInstances
  `${MEMBER}/patients/:patientId/flexible_visit_broadcast`, // FlexibleVisitBroadcasts
  `${MEMBER}/patients/:patientId/weekly_template_shifts`, // PatientWeeklyTemplateShiftList
  `${MEMBER}/patient/:patientId/caregivers_associated`, // PatientCaregiversAssociated
  `${MEMBER}/certifications_settings`, // AgencyCertificationsSettings
  `${AGENCY}/settings`, // AgencySettings
];

const CAREGIVER_MODAL_ENDPOINTS = [
  `${MEMBER}/caregivers/:caregiverId/visit_instances_billing`, // CaregiverCalendarData
  `${MEMBER}/certifications_settings`,
  `${AGENCY}/settings`,
];

/**
 * Ordered most-expensive-first so that if the browser stops handing us idle time,
 * the compiles we did get through are the ones that hurt most on the critical path.
 */
function toTargets(paths: string[]) {
  return paths.map((path) => ({ method: "GET", path }));
}

export function warmValidatorsForDeepLink(search: string = window.location.search) {
  const params = new URLSearchParams(search);

  if (params.get("patient") !== null) {
    warmValidators(toTargets(PATIENT_MODAL_ENDPOINTS));
    return;
  }

  if (params.get("caregiver") !== null) {
    warmValidators(toTargets(CAREGIVER_MODAL_ENDPOINTS));
  }
}
